import { css } from 'linaria';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { getImageCacheUrl } from 'common/utilities';
import { NewViewIcon } from 'components/icons/NewViewIcon';
const cardMediaStyles = "c97c5c";
/**
 * Each card contains image/video thumbnails.  This is a separate component so we
 * can handle imperfect content gracefully.
 *
 * The media is presented in a ratio of 3:2, but actual width is determined by
 * the container.  This module simple represents the graceful fallback as opposed
 * to the layout.
 */

export const CardMedia = function ({
  image_src,
  title,
  id,
  onImageFail = () => {},
  topicName,
  openUrl,
  onOpen,
  openInNewTab
}) {
  /**
   * Fallback images:
   * useFallback checks for imageLoad and if it fails, provides a class
   * that adds a :before and :after to the element for background/letter
   * it also passes back a derived color and letter to add to the style of
   * the containing element to avoid multiple CSS declarations being generated.
   *
   * NOTE: --fallbackBackground uses 8digit hex codes to provide alpha on the
   * background. #RRGGBBAA (red, green, blue, alpha). Important to note these
   * are still hex codes for alpha values so for example:
   *   100%  = FF
   *   90%	 = E6
   *   80%	 = CC
   *   70%	 = B3
   *   60%	 = 99
   *   50%	 = 80
   *   40%	 = 66
   *   30%	 = 4D
   *   20%	 = 33
   *   10%	 = 1A
   *   5%	   = 0D
   *   0%	   = 00
   */
  const hasImage = !!image_src;
  const letter = getFirstLetter(title);
  const color = getColorFromId(id);
  const mediaFallbackDetails = {
    '--fallbackBackground': `${color}80`,
    '--fallbackColor': `${color}`,
    '--fallbackLetter': `'${letter}'`
  };
  const {
    t
  } = useTranslation();
  const linkTarget = openInNewTab ? '_blank' : '';
  const linkRel = openInNewTab ? 'noopener noreferrer' : '';

  const MediaImage = () => {
    return hasImage ? <img style={mediaFallbackDetails} onError={onImageFail} alt="" src={getImageCacheUrl(image_src, {
      width: 600,
      height: 400
    })} srcSet={` ${getImageCacheUrl(image_src, {
      width: 1200,
      height: 800
    })} 2x `} /> : <div className="no-image" style={mediaFallbackDetails} />;
  };
  /**
   * We are setting these explicitly to avoid the image getting blocked by
   * ad/tracking blockers. They flag images set by JS as block-worthy
   */


  return <div className={`${cardMediaStyles} media`}>
      {topicName ? <div className="topic-name">{topicName}</div> : null}
      {openUrl ? <Link href={openUrl ? openUrl : false}>
          <a tabIndex="-1" data-cy="image-link" onClick={onOpen} target={linkTarget} rel={linkRel}>
            <MediaImage />
            {openInNewTab && openUrl ? <span className="original-view" data-cy="original-view">
                <span className="original-view-text">
                  {t('item-action:original-view', 'Original View')}
                </span>
                <NewViewIcon />
              </span> : null}
          </a>
        </Link> : <span className="no-link">
          <MediaImage />
        </span>}
    </div>;
};
/**
 * Avoid non alphanmericals
 * @param {string} word
 */

function getFirstLetter(word) {
  const firstLetterRegEx = /^(\W?)(\w)?/gu;
  const firstLetter = firstLetterRegEx.exec(word)[2];
  return firstLetter ? firstLetter.toUpperCase() : false;
}
/**
 * Get a consistent color based on the item_id This should allow us
 * to be consistent across platforms (in theory)
 * @param {string} id
 */


function getColorFromId(id) {
  const _colorCoral = '#EF4056';
  const _colorTeal = '#1CB0A8';
  const _colorAmber = '#FCB643';
  const _colorMint = '#00CB77';
  const colorArray = [_colorCoral, _colorTeal, _colorAmber, _colorMint, _colorAmber, _colorMint, _colorCoral, _colorTeal];
  const idInt = parseInt(id, 10); // Item Id may not be an int

  const colorIndex = idInt % colorArray.length;
  return colorArray[colorIndex || 0];
}

require("./card-media.linaria.module.css!=!../../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./card-media.js");